import React, { createContext, useContext, useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

const translations = {
  fr: {
    months: [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ],
    survey: {
      title: "Votre avis nous intéresse",
      subtitle: "Aidez-nous à améliorer notre service en répondant à ces quelques questions",
      submitError: "Une erreur s'est produite lors de la soumission de votre retour",
      takeSurvey: "Donner mon avis",
      ease: {
        question: "Comment avez-vous trouvé le processus d'identification ?",
        complicated: "Compliqué",
        notVeryEasy: "Pas très facile",
        easy: "Facile",
        veryEasy: "Très facile",
      },
      recommend: {
        question: "Quelle est la probabilité que vous recommandiez cette identification à d'autres propriétaires ou chauffeurs ?",
        notLikely: "Peu probable",
        somewhatLikely: "Assez probable",
        likely: "Probable",
        veryLikely: "Très probable",
      },
      comments: {
        label: "Commentaires supplémentaires",
        placeholder: "Partagez votre expérience, suggestions ou remarques...",
      },
      thankYou: {
        title: "Merci !",
        message: "Votre retour nous aide à améliorer nos services. Nous vous remercions pour votre temps.",
        continue: "Continuer",
      },
      skip: "Passer",
      submit: "Envoyer",
      note: "Vos réponses sont anonymes et nous aident à améliorer nos services.",
      requiredEase: "Veuillez indiquer comment vous avez trouvé le processus d'identification",
    },

    phoneValidation: {
      required: "Le numéro de téléphone est requis",
      invalidLength: "Le numéro doit contenir exactement 9 chiffres",
      invalidPrefix: "Le numéro doit commencer par 6 (mobile) ou 2 (fixe)",
      invalidCameroon: "Numéro de téléphone camerounais invalide",
      operatorNotAllowed: "Les numéros {{operator}} ne sont pas acceptés",
      mtn: "MTN",
      orange: "Orange",
      nextel: "Nextel",
      camtel: "Camtel",
      unknown: "Inconnu",
      formatHint: "Format: 6XX XX XX XX ou 2XX XX XX XX",
      examples: "Exemples: 677123456, 222345678"
    },
    fileValidation: {
      fileTooLarge: "Le fichier \"{{fileName}}\" est trop volumineux. Taille maximale: {{maxSize}}.",
      fileTooLargeGeneric: "Le fichier sélectionné est trop volumineux. La taille maximale autorisée est de 15MB.",
      invalidFileType: "Le fichier \"{{fileName}}\" n'est pas au format accepté. Formats autorisés: {{allowedTypes}}.",
      errorTitle: "Fichier trop volumineux",
      fileName: "Fichier",
      fileSize: "Taille",
      helpTitle: "📌 Solutions",
      solution1: "Compressez le fichier avant de le télécharger",
      solution2: "Prenez une photo avec une résolution plus faible",
      solution3: "Utilisez un fichier PDF de taille réduite",
      chooseAnother: "Choisir un autre fichier",
      maxSizeHint: "Taille maximale: 15MB",
      allowedFormats: "Formats acceptés: PDF, JPG, PNG",
      backendFileTooLarge: "Le fichier est trop volumineux pour le serveur. Taille maximum: 15MB.",
      fileTooLarge: "Fichier trop volumineux. Taille maximale: {{maxSize}}",
      backendFileTooLarge: "Le fichier est trop volumineux pour le serveur. Taille maximale: 15MB",
      maxSizeHint: "Taille maximale: 15MB",
      allowedFormats: "Formats acceptés: PDF, JPG, PNG",
      onlyImagesAllowed: "Seules les images sont autorisées pour la photo",
      pdfOrImagesAllowed: "Seuls les PDF et images sont autorisés",
      imageFormats: "JPG, PNG",
      pdfImageFormats: "PDF, JPG, PNG",
      acceptedFormats: "Formats acceptés"
    },
    procurationModal: {
      title: "Document de procuration requis",
      noteTitle: "📌 Important",
      noteText: "Vous pouvez continuer sans procuration, mais vous ne pourrez pas obtenir le QR code du véhicule. Vous devrez fournir ce document ultérieurement pour finaliser la vérification du véhicule.",
      uploadNow: "Ajouter la procuration",
      uploadHint: "PDF, JPG ou PNG",
      continueWithout: "Continuer sans procuration",
      continueHint: "QR code véhicule non généré",
    },
    common: {
      continue: "Continuer",
      required: "Champ obligatoire",
      invalidDate: "Date invalide (JJ/MM/AAAA)",
      yes: "Oui",
      no: "Non",
      cancel: "Annuler",
      close: "Fermer",
      ok: "OK",
      remove: "Supprimer",
      save: "Enregistrer",
      saving: "Enregistrement...",
      saveDriver: "Enregistrer le chauffeur",
      uploading: "Téléchargement...",
      undo: "Annuler",
      optional: "optionnel",
      success: "Succès",
      info: "Information",
      skip: "Passer"
    },
    vehicleAdmin: {
      title: "Informations administratives du véhicule",
      subtitle: "Renseignez les informations administratives du véhicule",
      techInspectionValid: "Le véhicule a-t-il un certificat de visite technique en cours de validité (vérifier la date moins de 3 mois) ?",
      techInspectionDate: "Date de la visite technique (JJ/MM/AAAA)",
      techInspection: "Visite technique",
      blueCardNumber: "Numéro de la carte bleue à laquelle est rattaché le véhicule",
      blueCardNumberPlaceholder: "Numéro de la carte bleue",
      blueCardIssueDate: "C5 - Date de délivrance de la carte bleue (JJ/MM/AAAA)",
      ownerCatLicenseNumber: "C6 - Numéro de la licence de première catégorie du propriétaire",
      ownerCatLicenseNumberPlaceholder: "Numéro de la licence catégorie",
      ownerCatLicenseIssueDate: "C7 - Date de délivrance de cette licence (JJ/MM/AAAA)",
      municipality: "C8 - Mairie d'attachement du véhicule",
      municipalityTaxNumber: "C9 - Numéro de l'impôt libératoire du véhicule",
      municipalityTaxNumberPlaceholder: "Numéro fiscal municipal",
      taxisOperated: "C10 - Combien de taxis exploite le propriétaire ?",
      taxisOperatedPlaceholder: "Nombre de taxis exploités",
      driverPhone: "Numéro de téléphone du chauffeur du véhicule",
      driverPhonePlaceholder: "Téléphone du conducteur",
      driverCapacityNumber: "D5 - Numéro de la capacité du chauffeur",
      driverCapacityNumberPlaceholder: "Numéro de capacité",
      secureBadge: "D6 - Le chauffeur possède-t-il un badge sécurisé d'identification ?",
      driversRelayCount: "D7 - Combien de chauffeurs se relaient sur ce véhicule ?",
      driversRelayCountPlaceholder: "Nombre de relais de conducteurs",
      macaronNumber: "E1 - Numéro de macaron délivré",
      macaronNumberPlaceholder: "Numéro du macaron",
      arr1: "Commune arrondissement 1",
      arr2: "Commune arrondissement 2",
      arr3: "Commune arrondissement 3",
      arr4: "Commune arrondissement 4",
      arr5: "Commune arrondissement 5",
      arr6: "Commune arrondissement 6",
      arr7: "Commune arrondissement 7",
      vehicle: "Véhicule",
      owner: "Propriétaire",
      driver: "Conducteur",
      invalidNumber: "Nombre invalide",
      form: "Formulaire",
      correctFields: "Veuillez corriger les champs requis.",
      error: "Erreur",
      serverError: "Impossible d'enregistrer. Vérifiez le serveur.",
      saving: "Enregistrement...",
      driverName: "Nom",
      driverEmail: "Email",
      driverBirthDate: "Date de naissance",
      driverGender: "Sexe",
      driverCin: "Numéro de CIN",
      driverTaxpayer: "Numéro fiscal",
      driverLicense: "Numéro de permis",
      driversRequired: "Au moins un conducteur est requis",
      ownerSyndicate: "Syndicat du propriétaire",
      ownerSyndicatePlaceholder: "Syndicat du propriétaire",
      
      // ✅ NEW: Driver found translations
      driverFound: "Chauffeur trouvé !",
      driverFoundMessage: "Vous êtes déjà enregistré comme chauffeur pour ce véhicule sous le nom {{name}}.",
      driverInfo: "Informations du chauffeur",
      theDriver: "le chauffeur",
      licenseNumber: "Numéro de permis",
    },
    home: {
      serviceNotAvailable: "SERVICE TEMPORAIREMENT INDISPONIBLE",
      title: "IDENTIFICATION DES TAXIS",
      subtitle: "VILLE DE YAOUNDÉ",
      description: "Propriétaires et chauffeurs, identifiez-vous pour professionnaliser et moderniser le transport urbain.",
      startIdentification: "COMMENCER L'IDENTIFICATION",
      whoIsConcerned: "QUI EST CONCERNÉ ?",
      ownerTitle: "PROPRIÉTAIRE TAXI",
      ownerDescription: "Détenteur du véhicule et des documents administratifs.",
      driverTitle: "CONDUCTEUR TAXI",
      driverDescription: "Chauffeur en activité avec documents professionnels valides.",
      documentsRequired: "PIÈCES À FOURNIR",
      forOwner: "POUR LE PROPRIÉTAIRE",
      forDriver: "POUR LE CONDUCTEUR",
    // Document list for owner
      ownerDocs: [
        "Carte Nationale d'Identité",
        "Carte Grise (Certificat d'Immatriculation)",
        "Licence de Transport Ordinaire de Première Catégorie",
        "Carte de Transport Public",
        "Visite Technique Taxi",
        "Assurance Quatrième Catégorie A",
        "Impôt Général Synthétique (IGS)",
      ],

      // Document list for driver
      driverDocs: [
        "Carte Nationale d'Identité",
        "Permis de Conduire Catégorie B",
        "Certificat de Capacité Modèle T",
        "Carte de Résidence (pour les étrangers)",
        "Photo d'Identité",
        "Badge Professionnel d'Identification",
      ],
      whyCensus: "POURQUOI CE PROCESSUS D'IDENTIFICATION ?",
      whyDescription: "Cette initiative stratégique de la Communauté Urbaine de Yaoundé vise à :",
      // Why census points
      whyPoints: [
        "Assainir le secteur du transport urbain",
        "Garantir la sécurité des usagers",
        "Améliorer les conditions de travail des professionnels",
        "Digitaliser la gestion du parc automobile",
        "Créer une base de données fiable pour la planification",
      ],
      freeProcess: "PROCESSUS 100% GRATUIT",
      freeBadge: "OFFICIEL",
      freeSubtitle: "Pour cette période initiale",
      freeDescription: "L'enregistrement est pour le moment gratuit. Les agents habilités portent des badges d'identification officiels. Méfiez-vous des individus qui demandent de l'argent.",
      officialPartners: "PARTENAIRES OFFICIELS",
      moreInfoTitle: "POUR PLUS D’INFORMATIONS",
      // Partner names
      partners: ["MINHDU", "GIZ", "AFD", "UNION EUROPÉENNE"],
      identifyNow: "IDENTIFIEZ-VOUS MAINTENANT",
      clickToAccess: "Cliquez pour accéder au formulaire en ligne",
      footerNote: "Service officiel de la Communauté Urbaine de Yaoundé • Sécurité des données garantie",
      contacts: "CONTACTS & ASSISTANCE",
      cuyDirectLine: "CUY - LIGNE DIRECTE",
      unionPresidents: "ÉQUIPE TECHNIQUE",
      copyright: "© 2026 COMMUNITE URBAINE DE YAOUNDÉ • TOUS DROITS RÉSERVÉS",
      version: "Version 1.0 • Dernière mise à jour : {{month}} {{year}}",
    },
    ownership: {
      title: "Propriétaire du véhicule",
      subtitle: "Cette information est nécessaire pour adapter la procédure de vérification.",
      yesDescription: "Je suis le propriétaire du véhicule",
      noDescription: "Je conduis un véhicule appartenant à une autre personne",
      helperText: "Vous pourrez fournir les documents nécessaires à l'étape suivante.",
    },
    ownerForm: {
      title: "Informations du propriétaire",
      subtitle: "Renseignez le nom du propriétaire et ajoutez une copie de sa pièce d'identité.",
      firstName: "Prénom",
      firstNamePlaceholder: "Ex: Jean",
      lastName: "Nom",
      lastNamePlaceholder: "Ex: Onana",
      addIdCard: "Ajouter la pièce d'identité",
      fileFormats: "PDF, PNG, JPG",
      remove: "Supprimer",
    },
    nonOwnerForm: {
      title: "Représentation du propriétaire",
      subtitle: "Veuillez renseigner les informations nécessaires",
      driver: "Conducteur",
      driverFirstNamePlaceholder: "Prénom",
      driverLastNamePlaceholder: "Nom",
      owner: "Propriétaire",
      physicalPerson: "Personne physique",
      company: "Société",
      ownerFirstNamePlaceholder: "Prénom du propriétaire",
      ownerLastNamePlaceholder: "Nom du propriétaire",
      companyNamePlaceholder: "Nom de la société",
      procuration: "Procuration",
      addDocument: "Ajouter un document",
      fileFormats: "PDF, JPG ou PNG",
    },
    verification: {
      title: "Vérification du véhicule",
      subtitle: "Entrez le numéro d'immatriculation ou de châssis",
      inputLabel: "Numéro d'immatriculation / châssis",
      inputPlaceholder: "Ex : CE153LC ou CH12345678901234567",
      searchButton: "Rechercher",
      invalidFormat: "Format invalide. Veuillez entrer un numéro d'immatriculation ou de châssis valide.",
      networkError: "Erreur réseau. Veuillez vérifier votre connexion et réessayer.",
      ownerInfoIncomplete: "Les informations du propriétaire ne sont pas complètes. Veuillez les renseigner avant de continuer.",
      serverResponseInvalid: "Réponse serveur invalide. Veuillez réessayer.",
      serverError: "Erreur serveur pendant la création du dossier de vérification.",
      serverErrorDossier: "Erreur serveur pendant la création du dossier.",
      resultFound: "Résultat trouvé",
      registration: "Immatriculation",
      insured: "Assuré",
      expiry: "Échéance",
      infoMatches: "Les informations correspondent.",
      infoDoesNotMatch: "Les informations ne correspondent pas.",
      continueVerification: "Continuer la vérification",
      vehicleNotFound: "Véhicule introuvable",
      vehicleNotFoundMessage: "Le véhicule n'a pas été trouvé dans la base de données.\nIl sera enregistré comme une nouvelle immatriculation.",
      input: "Saisie",
      registeredOwners: "Propriétaires enregistrés",
      
      // ✅ NEW: Driver found verification translations
      driverFound: "Chauffeur trouvé",
      driverFoundMessage: "Vous êtes déjà enregistré comme chauffeur pour ce véhicule.",
      licenseNumber: "Permis",
    },
    consent: {
      title: "Consentement au traitement des données",
      subtitle: "Conformément à la loi n°2024/017 du 23 décembre 2024",
      viewAgreement: "Voir l'accord complet",
      agreementText: "Je consens au traitement de mes données personnelles dans le cadre de la procédure de vérification du véhicule.",
      acceptConditions: "J'accepte les conditions",
      accept: "Accepter",
      fullAgreementTitle: "Accord de traitement des données",
      readFullAgreement: "Lire l'accord complet",
    },
    documentUpload: {
      registrationTitle: "Carte Grise",
      registrationSubtitle: "Ajoutez les documents de la carte grise du véhicule",
      transportTitle: "Carte de transport public",
      transportSubtitle: "Ajoutez la carte de transport public (carte bleue)",
      taxReceiptTitle: "Reçu d'impôts libératoires",
      taxReceiptSubtitle: "Ajoutez le reçu de paiement des impôts libératoires",
      licenceTitle: "Licence de transport",
      licenceSubtitle: "Ajoutez la licence ordinaire de transport",
      cameraPermissionDenied: "Permission caméra refusée.",
      noVerificationFound: "Aucun dossier de vérification trouvé.",
      addAtLeastOne: "Veuillez ajouter au moins un document.",
      uploadError: "Erreur lors de l'upload de la carte grise.",
      transportUploadError: "Erreur lors de l'upload de la carte de transport.",
      taxReceiptUploadError: "Erreur lors de l'upload du reçu de paiement des impôts libératoires.",
      licenceUploadError: "Erreur lors de l'upload de la licence de transport.",
      takePhoto: "Prendre une photo",
      photoHint: "Photographiez la carte grise",
      transportPhotoHint: "Photographiez la carte de transport",
      taxReceiptPhotoHint: "Photographiez le reçu d'impôts",
      licencePhotoHint: "Photographiez la licence",
      importFile: "Importer un fichier",
      fileFormats: "PDF, PNG ou JPG",
      remove: "Supprimer",
      next: "Suivant",
      skip: "Passer (je n'ai pas ce document)",
      takePhoto: "Prendre une photo",
      importFile: "Importer un fichier",
      cameraPermissionDenied: "Permission de caméra refusée",
      selectionError: "Erreur lors de la sélection du fichier",
      uploadError: "Erreur lors du téléchargement",
      skip: "Passer",
      importImage: "Importer une image",
    },
    completion: {
      doc1: "Carte de Transport Public (Carte Bleue)",
      doc2: "Carte Grise du Véhicule",
      doc3: "Reçu de Paiement des Impôts Libératoires",
      doc4: "Licence Ordinaire de Transport",
      completedBadge: "✅ Terminé",
      submittedTitle: "Dossier soumis\npour propriétaire, conducteur et véhicule",
      requestedTitle: "Étape de dépôt des documents terminée",
      submittedSubtitle: "Votre dossier a été soumis avec succès. Vous recevrez une notification lorsque vos QR codes seront générés.",
      pendingTitle: "Génération du QR Code.",
      pendingMessage: "Vos QR codes sont en cours de génération. Vous recevrez une notification lorsque ceux-ci seront prêts.",
      requestedSubtitle_part1: "Vous avez terminé l'étape de dépôt des documents requis.",
      documentAdded: "Document ajouté",
      completeMessage: "Votre dossier est désormais complet.",
      errorMessage: "Erreur lors de la finalisation du dossier.",
      finish: "Terminer",
      startNewForm: "Commencer un nouveau formulaire",
      finishVerification: "Terminez l’Identification",
      qrCodeTitle: "QR Code du véhicule",
      qrCodeDescription: "Votre QR code unique pour la vérification numérique",
      downloadQRCode: "Télécharger le QR Code du véhicule (PDF)",
      downloadQRTitle: "Télécharger le QR Code du véhicule",
      downloadQRMessage: "Voulez-vous télécharger le QR code en format PDF?",
      download: "Télécharger",
      downloadSuccess: "Téléchargement réussi",
      downloadSuccessMessage: "Le QR code a été téléchargé avec succès",
      downloadError: "Erreur lors du téléchargement",
      completeError: "Erreur lors de la finalisation de la vérification",
      docsStatusTitle: "Statut des documents",
      driverQrTitle: "QR Code du chauffeur",
      downloadDriverQr: "Télécharger le QR Code du chauffeur",
      yourDriverQrTitle: "Votre QR Code de chauffeur",
      driverLabel: "Driver: {{id}}",
      infoTitle: "ℹ️ Information",
      driverOnlyQrInfo:"Seul votre QR code de chauffeur a été généré. Le QR code du véhicule ne peut être généré qu'après la fourniture de la procuration.",
      summaryTitle: "Récapitulatif de votre demande",
      summaryDescription: "Téléchargez un document récapitulatif contenant toutes les informations que vous avez fournies, ainsi que le statut de vos documents.",
      downloadSummary: "Télécharger le récapitulatif",
      pdfGenerated: "Le récapitulatif PDF a été généré avec succès",
      pdfError: "Erreur lors de la génération du PDF récapitulatif",
  docs: {
    carRegistration: "Carte Grise",
    transportCard: "Carte de Transport",
    taxReceipt: "Reçu d'Impôts",
    transportLicence: "Licence de Transport",
    procuration: "Procuration",

    status: {
      ignored: "Document ignoré",
      notProvided: "Non fourni",
      notProvidedFemale: "Non fournie",
      provided: "Fournie",
      filesCount: "{{count}} fichier(s)",
    },

    blockTitle: "⚠️ Attention",
    back: "Retour",
    continue: "Continuer",
  },
vehicleQrBlockedReason: "Le QR code du véhicule ne peut pas être généré tant que la procuration n'est pas fournie.",

    },
    install: {
      title: "Installer l'application",
      iosInstructions: "Sur iPhone/iPad : Appuyez sur Partager → Ajouter à l'écran d'accueil.",
      promptMessage: "Installez l'application sur votre appareil pour un accès plus rapide et un support hors ligne.",
      manualInstructions: "Pour installer : ouvrez le menu du navigateur ⋮ et choisissez Installer l'application.",
      installNow: "Installer maintenant",
      later: "Plus tard",
    },
    payment: {
      title: "Timbre Communal",
      subtitle: "Loi n° 2017/015 du 20 décembre 2017 fixant le tarif actuel à 500 FCFA.",
      amount: "Montant à payer",
      amountHint: "Frais de traitement",
      selectProvider: "Sélectionnez votre opérateur",
      mtnHint: "MTN Mobile Money",
      orangeHint: "Orange Money",
      phoneNumber: "Numéro de téléphone",
      phoneNumberHint: "Entrez votre numéro Mobile Money ou Orange Money",
      invalidPhoneNumber: "Numéro invalide. Format: 6XX XXX XXX ou 2XX XXX XXX",
      validPhoneNumber: "Numéro valide",
      payNow: "Payer {amount} FCFA",
      paymentInitiated: "Paiement initié",
      confirmOnPhone: "Un message de confirmation a été envoyé au {provider}. Veuillez saisir votre code PIN sur votre téléphone pour confirmer le paiement de {amount} FCFA.",
      waitingConfirmation: "En attente de confirmation...",
      continueToNext: "Continuer vers l'étape suivante",
      verificationId: "ID de vérification",
      instructionsTitle: "Instructions importantes",
      instructions: "1. Sélectionnez votre opérateur (MTN ou Orange)\n2. Entrez votre numéro de téléphone Mobile Money\n3. Cliquez sur 'Payer maintenant'\n4. Confirmez le paiement sur votre téléphone\n5. Attendez la confirmation automatique",
      statusSuccess: "✅ Paiement confirmé",
      statusPending: "⏳ En attente de confirmation",
      statusFailed: "❌ Échec du paiement",
      statusHint: "Vérification en cours...",
      error: "Erreur",
      enterPhoneNumber: "Veuillez entrer votre numéro de téléphone",
      noVerificationId: "Aucun ID de vérification trouvé",
      initiationFailed: "Échec de l'initiation du paiement",
      networkError: "Erreur réseau. Veuillez réessayer.",
      waitForConfirmation: "Veuillez attendre la confirmation du paiement",
      successTitle: "Paiement réussi",
      successMessage: "Votre paiement de {amount} FCFA a été confirmé avec succès.",
      paymentFailed: "Le paiement a échoué ou a été annulé. Veuillez réessayer.",
      alreadyPaidTitle: "Paiement déjà effectué",
      alreadyPaidMessage: "Vous avez déjà payé pour cette vérification. Passage à l'étape suivante...",
      checkingPaymentStatus: "Vérification du statut du paiement...",
    },
    vehicleNotFoundForm: {
      title: "Véhicule introuvable",
      titleNotMatched: "Registrer le véhicule",
      subtitle: "Renseignez toutes les informations pour enregistrer le véhicule.",
      back: "Retour",
      enteredIdentifierTitle: "Identifiant saisi",
      ownerSectionTitle: "Informations du propriétaire",
      vehicleSectionTitle: "Informations du véhicule",
      ownerTypeLabel: "Type de propriétaire",
      ownerTypePhysical: "Personne physique",
      ownerTypeCompany: "Entreprise",
      ownerNameLabelPhysical: "Nom du propriétaire",
      ownerNameLabelCompany: "Nom de l'entreprise",
      ownerNamePlaceholderPhysical: "Ex: Jean Dupont",
      ownerNamePlaceholderCompany: "Ex: ETS NGONO",
      sexLabel: "Sexe",
      male: "Homme",
      female: "Femme",
      phoneLabel: "Téléphone",
      phonePlaceholder: "Ex: 6XX XXX XXX",
      emailLabel: "Email",
      emailPlaceholder: "Ex: jean.dupont@email.com",
      brandLabel: "Marque",
      brandOther: "Autre",
      specifyBrandLabel: "Précisez la marque",
      specifyBrandPlaceholder: "Ex: Mercedes",
      firstRegistrationDateLabel: "Date de première mise en circulation",
      firstRegistrationDatePlaceholder: "JJ/MM/AAAA",
      chassisLabel: "Numéro de châssis",
      chassisPlaceholder: "Ex: JT123...",
      seatsLabel: "Nombre de places",
      seatsPlaceholder: "Ex: 5",
      insuranceStickerLabel: "Macaron d'assurance en cours de validité ?",
      yes: "Oui",
      no: "Non",
      submit: "Continuer",
      invalidSeats: "Nombre de places invalide.",
      invalidFirstRegistrationDate: "Date de première immatriculation invalide (JJ/MM/AAAA ou AAAA-MM-JJ).",
      serverError: "Erreur serveur.",
      portierNumberLabel: "Numéro du portier",
      portierNumberPlaceholder: "Ex: 12345",
    },
    vehicleAdminDocs: {
      iDontHaveTechInspection: "Je n'ai pas la visite technique",
      iDontHaveBlueCard: "Je n'ai pas la carte bleue",
      iDontHaveOwnerLicense: "Je n'ai pas la licence (catégorie) du propriétaire",
      iDontHaveMunicipalityTax: "Je n'ai pas le numéro de taxe communale",
      iDontHaveDriverCapacity: "Je n'ai pas le numéro de capacité du chauffeur",
      iDontHaveMacaron: "Je n'ai pas le macaron",
      iDontHaveSecureBadge: "Je n'ai pas le badge sécurisé",
    },
    errors: {
      invalidName: "Nom invalide",
    },
    driverManager: {
      title: "Chauffeurs",
      addDriver: "+ Ajouter un chauffeur",
      loadingDrivers: "Chargement des chauffeurs...",
      driverTitle: "Chauffeur {{number}}",
      existingInDatabase: " (Existant dans la base de données)",
      modified: " (Modifié)",
      cannotRemove: "Impossible de supprimer",
      cannotRemoveMessage: "Ce chauffeur existe déjà dans la base de données et ne peut pas être supprimé d'ici.",
      searchPlaceholder: "Rechercher par CIN ou Numéro de Permis",
      driverFound: "Chauffeur trouvé",
      driverFoundMessage: "Les informations du chauffeur existant ont été chargées.",
      driverSyndicate: "Syndicat du chauffeur",
      driverSyndicatePlaceholder: "Syndicat du chauffeur",
      alreadySaved: "Déjà enregistré",
      alreadySavedMessage: "Ce chauffeur a déjà été enregistré.",
      saved: "Enregistré",
      alreadySavedInfo: "Ce chauffeur a été enregistré avec succès. Vous pouvez continuer.",
      noLongerDriver: "Plus un chauffeur de ce véhicule",
      sections: {
        basicInfo: "Informations de base",
        licenseInfo: "Informations du permis",
        capacityInfo: "Capacité professionnelle",
        secureBadge: "Badge de sécurité",
        documents: "Documents"
      },
      fields: {
        fullName: "Nom Complet *",
        phone: "Téléphone *",
        email: "Email",
        birthDate: "Date de Naissance (JJ/MM/AAAA)",
        gender: "Genre",
        cinNumber: "Numéro CIN",
        taxpayerNumber: "Numéro Contribuable",
        licenseNumber: "Numéro de Permis",
        driverSyndicate: "Syndicat du chauffeur",
        licenseCategory: "Catégorie de permis",
        selectCategory: "Sélectionner une catégorie",
        capacityNumber: "Numéro de capacité",
        capacityNumberPlaceholder: "Ex: CAP-12345",
        secureBadge: "Badge de sécurité"
      },
      dontHaveCapacity: "Je n'ai pas de document de capacité",
      dontHaveSecureBadge: "Je n'ai pas de badge de sécurité",
      genders: {
        male: "Homme",
        female: "Femme"
      },
      documents: {
        photo: "Photo du chauffeur",
        license: "Permis de conduire",
        idCard: "Carte d'identité",
        capacity: "Document de capacité",
        skipped: "Passé (document non fourni)"
      },
        documentUploaded: "Document téléchargé avec succès",
        documentStoredLocally: "Document sélectionné. Il sera sauvegardé quand le chauffeur sera enregistré.",
        saveToUpload: "Enregistrez d'abord le chauffeur pour télécharger les documents",
        driverAndDocsSaved: "Chauffeur et documents enregistrés avec succès",
        allDriversSaved: "Tous les chauffeurs ont été enregistrés avec succès",
        uploadDocumentsHint: "Téléchargez les documents du chauffeur",
        driverSaved: "Chauffeur enregistré avec succès",
        
        // Status
        saved: "Enregistré",
        uploading: "Téléchargement...",
      
      // ✅ NEW: Driver form errors
      errors: {
        invalidPhone: "Numéro de téléphone invalide"
      }
    },
    vehicleNotMatched: {
      title: "Propriétaire non correspondant",
      subtitle: "Les informations du propriétaire ne correspondent pas. Veuillez fournir vos informations complètes.",
      enteredIdentifierTitle: "Identifiant du véhicule",
      existingOwnerTitle: "Propriétaire enregistré",
      newOwnerTitle: "Nouvelles informations du propriétaire",
      ownerTypeLabel: "Type de propriétaire",
      idCardTitle: "Pièce d'identité",
      takePhoto: "Prendre une photo",
      uploadFile: "Importer un fichier",
      idCardHelper: "Format accepté : PDF, JPG, PNG",
      modalMessage: "Le propriétaire enregistré ne correspond pas à vos informations. Veuillez fournir vos documents d'identité pour continuer.",
      provideDocuments: "Fournir mes documents",
      ownerSyndicateLabel: "Syndicat du propriétaire",
      ownerSyndicatePlaceholder: "Syndicat du propriétaire",
    },
    
    // ✅ NEW: Driver Form translations
    driverForm: {
      addYourself: "Ajoutez-vous comme chauffeur",
      subtitle: "Veuillez fournir vos informations pour être ajouté comme chauffeur de ce véhicule"
    },
    
  },






  en: {
    months: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],

    docsStatusTitle: "Document status",
    docs: {
      carRegistration: "Registration Certificate",
      transportCard: "Transport Card",
      taxReceipt: "Tax Receipt",
      transportLicence: "Transport License",
      procuration: "Power of attorney",
      status: {
        ignored: "Document skipped",
        notProvided: "Not provided",
        notProvidedFemale: "Not provided",
        provided: "Provided",
        filesCount: "{{count}} file(s)",
      },
      blockTitle: "⚠️ Warning",
      back: "Back",
      continue: "Continue",
    },
    requestedSubtitle_part2: "Please check the missing documents below before ",
    vehicleQrNotGeneratedTitle: "⚠️ Vehicle QR not generated",
    driverQrTitle: "Driver QR Code",
    driverQrPluralSuffix: "s",
    driverQrDescription: "Show this QR code to identify the driver(s).",
    yourDriverQrTitle: "Your Driver QR Code",
    driverLabel: "Driver: {{id}}",
    downloadDriverQr: "Download QR Code",
    infoTitle: "ℹ️ Information",
    driverOnlyQrInfo:
      "Only your driver QR code was generated. The vehicle QR code can only be generated after providing the procuration.",
    survey: {
      title: "We value your feedback",
      subtitle: "Help us improve our service by answering a few questions",
      submitError: "An error occurred while submitting your feedback",
      takeSurvey: "Take Survey",
      ease: {
        question: "How easy was the identification process?",
        complicated: "Complicated",
        notVeryEasy: "Not very easy",
        easy: "Easy",
        veryEasy: "Very easy",
      },
      recommend: {
        question: "How likely are you to recommend this identification process to other owners or drivers?",
        notLikely: "Not likely",
        somewhatLikely: "Somewhat likely",
        likely: "Likely",
        veryLikely: "Very likely",
      },
      comments: {
        label: "Additional comments",
        placeholder: "Share your experience, suggestions, or feedback...",
      },
      thankYou: {
        title: "Thank You!",
        message: "Your feedback helps us improve our services. We appreciate your time.",
        continue: "Continue",
      },
      skip: "Skip",
      submit: "Submit",
      note: "Your responses are anonymous and help us improve our services.",
      requiredEase: "Please indicate how easy you found the identification process",
    },
    phoneValidation: {
      required: "Phone number is required",
      invalidLength: "Number must contain exactly 9 digits",
      invalidPrefix: "Number must start with 6 (mobile) or 2 (landline)",
      invalidCameroon: "Invalid Cameroon phone number",
      operatorNotAllowed: "{{operator}} numbers are not accepted",
      mtn: "MTN",
      orange: "Orange",
      nextel: "Nextel",
      camtel: "Camtel",
      unknown: "Unknown",
      formatHint: "Format: 6XX XX XX XX or 2XX XX XX XX",
      examples: "Examples: 677123456, 222345678"
    },
    fileValidation: {
      fileTooLarge: "File \"{{fileName}}\" is too large. Maximum size: {{maxSize}}.",
      fileTooLargeGeneric: "The selected file is too large. Maximum allowed size is 15MB.",
      invalidFileType: "File \"{{fileName}}\" is not in an accepted format. Allowed formats: {{allowedTypes}}.",
      errorTitle: "File Too Large",
      fileName: "File",
      fileSize: "Size",
      helpTitle: "📌 Solutions",
      solution1: "Compress the file before uploading",
      solution2: "Take a photo with lower resolution",
      solution3: "Use a smaller PDF file",
      chooseAnother: "Choose another file",
      maxSizeHint: "Maximum size: 15MB",
      allowedFormats: "Accepted formats: PDF, JPG, PNG",
      backendFileTooLarge: "File is too large for the server. Maximum size: 15MB.",
      fileTooLarge: "File too large. Maximum size: {{maxSize}}",
      backendFileTooLarge: "File is too large for the server. Maximum size: 15MB",
      maxSizeHint: "Maximum file size: 15MB",
      allowedFormats: "Accepted formats: PDF, JPG, PNG",
      onlyImagesAllowed: "Only images are allowed for photos",
      pdfOrImagesAllowed: "Only PDF and images are allowed",
      imageFormats: "JPG, PNG",
      pdfImageFormats: "PDF, JPG, PNG",
      acceptedFormats: "Accepted formats",
    },
    procurationModal: {
      title: "Power of attorney document required",
      noteTitle: "📌 Important",
      noteText: "You can continue without power of attorney, but you will not be able to obtain the vehicle QR code. You will need to provide this document later to complete the vehicle verification.",
      uploadNow: "Add power of attorney document",
      uploadHint: "PDF, JPG or PNG",
      continueWithout: "Continue without power of attorney",
      continueHint: "Vehicle QR code not generated",
    },
    common: {
      continue: "Continue",
      required: "Required field",
      invalidDate: "Invalid date (DD/MM/YYYY)",
      yes: "Yes",
      no: "No",
      cancel: "Cancel",
      close: "Close",
      ok: "OK",
      remove: "Remove", 
      save: "Save",
      saving: "Saving...",
      saveDriver: "Save Driver",
      uploading: "Uploading...",
      undo: "Undo",
      optional: "optional",
      success: "Success",
      info: "Information",
      skip: "Skip",
    },
    vehicleAdmin: {
      title: "Vehicle administrative information",
      subtitle: "Fill in the vehicle's administrative information",
      techInspectionValid: "Is the technical inspection certificate still valid (check the date less than 3 months)?",
      techInspectionDate: "Technical inspection date (DD/MM/YYYY)",
      techInspection: "Technical inspection", 
      blueCardNumber: "Vehicle blue card number",
      blueCardNumberPlaceholder: "Blue card number",
      blueCardIssueDate: "C5 - Blue card issue date (DD/MM/YYYY)",
      ownerCatLicenseNumber: "C6 - Owner first category license number",
      ownerCatLicenseNumberPlaceholder: "Category license number",
      ownerCatLicenseIssueDate: "C7 - Issue date of this license (DD/MM/YYYY)",
      municipality: "C8 - Vehicle's town hall",
      municipalityTaxNumber: "C9 - Municipality tax number",
      municipalityTaxNumberPlaceholder: "Municipal tax number",
      taxisOperated: "C10 - How many taxis does the owner operate?",
      taxisOperatedPlaceholder: "Number of taxis operated",
      driverPhone: "Vehicle driver phone number",
      driverPhonePlaceholder: "Driver's phone",
      driverCapacityNumber: "D5 - Driver capacity number",
      driverCapacityNumberPlaceholder: "Capacity number",
      secureBadge: "D6 - Does the driver have a secure identification badge?",
      driversRelayCount: "D7 - How many drivers operate the vehicle?",
      driversRelayCountPlaceholder: "Number of driver relays",
      macaronNumber: "E1 - Registration badge number issued",
      macaronNumberPlaceholder: "Registration badge number",
      arr1: "Municipality arrondissement 1",
      arr2: "Municipality arrondissement 2",
      arr3: "Municipality arrondissement 3",
      arr4: "Municipality arrondissement 4",
      arr5: "Municipality arrondissement 5",
      arr6: "Municipality arrondissement 6",
      arr7: "Municipality arrondissement 7",
      vehicle: "Vehicle",
      owner: "Owner",
      driver: "Driver",
      invalidNumber: "Invalid number",
      form: "Form",
      correctFields: "Please correct the required fields.",
      error: "Error",
      serverError: "Unable to save. Check the server.",
      saving: "Saving...",
      driverName: "Name",
      driverEmail: "Email",
      driverBirthDate: "Birth Date",
      driverGender: "Gender",
      driverCin: "CIN Number",
      driverTaxpayer: "Taxpayer Number",
      driverLicense: "License Number",
      driversRequired: "At least one driver is required",
      ownerSyndicate: "Owner's Union",
      ownerSyndicatePlaceholder: "Owner's Union",
      
      // ✅ NEW: Driver found translations
      driverFound: "Driver found!",
      driverFoundMessage: "You are already registered as a driver for this vehicle under the name {{name}}.",
      driverInfo: "Driver Information",
      theDriver: "the driver",
      licenseNumber: "License Number",
    },
    home: {
      serviceNotAvailable: "SERVICE TEMPORARILY UNAVAILABLE",
      title: "IDENTIFICATION OF TAXIS",
      subtitle: "CITY OF YAOUNDE",
      description: "Owners and drivers, identify yourself to Professionalize and modernize urban transport.",
      startIdentification: "START IDENTIFICATION",
      whoIsConcerned: "WHO IS CONCERNED?",
      ownerTitle: "TAXI OWNER",
      ownerDescription: "Holder of the vehicle and administrative documents.",
      driverTitle: "TAXI DRIVER",
      driverDescription: "Driver with valid professional documents.",
      documentsRequired: "REQUIRED DOCUMENTS",
      forOwner: "FOR THE OWNER",
      forDriver: "FOR THE DRIVER",
     ownerDocs: [
        "National Identity Card",
        "Registration Certificate (Carte Grise)",
        "Ordinary First Category Transport License",
        "Public Transport Card",
        "Taxi Technical Inspection",
        "Fourth Category A Insurance",
        "General Synthetic Tax (IGS)",
      ],

      driverDocs: [
        "National Identity Card",
        "Category B Driving License",
        "Model T Capacity Certificate",
        "Residence Card (for foreigners)",
        "Identity Photo",
        "Professional Identification Badge",
      ],
      whyCensus: "WHY THIS PROCESS OF IDENTIFICATION",
      whyDescription: "Strategic initiative by the City of Yaoundé aims to:",
      whyPoints: [
        "Clean up the urban transport sector",
        "Ensure user safety",
        "Improve working conditions of professionals",
        "Digitalize vehicle fleet management",
        "Create a reliable database for planning",
      ],
      freeProcess: "100% FREE PROCESS",
      freeBadge: "OFFICIAL",
      freeDescription: "Registration is currently free. Authorized agents carry official identification badges. Beware of individuals who ask for money.",
      officialPartners: "OFFICIAL PARTNERS",
      moreInfoTitle: "FOR MORE INFORMATION",
      partners: ["MINHDU", "GIZ", "AFD", "EUROPEAN UNION"],
      identifyNow: "IDENTIFY YOURSELF NOW",
      clickToAccess: "Click to access the online form",
      footerNote: "Official service of the City of Yaoundé  • Data security guaranteed",
      contacts: "CONTACTS & ASSISTANCE",
      cuyDirectLine: "CUY - DIRECT LINE",
      unionPresidents: "TECHNICAL  TEAM ",
      copyright: "© 2026 CITY OF YAOUNDE • ALL RIGHTS RESERVED",
      version: "Version 1.0 • Last update: {{month}} {{year}}",
    },
    ownership: {
      title: "Vehicle Owner",
      subtitle: "This information is required to adapt the verification procedure.",
      yesDescription: "I am the owner of the vehicle",
      noDescription: "I drive a vehicle belonging to another person",
      helperText: "You will be able to provide the necessary documents in the next step.",
    },
    ownerForm: {
      title: "Owner Information",
      subtitle: "Enter the owner's name and add a copy of their ID document.",
      firstName: "First Name",
      firstNamePlaceholder: "Ex: John",
      lastName: "Last Name",
      lastNamePlaceholder: "Ex: Smith",
      addIdCard: "Add ID Card",
      fileFormats: "PDF, PNG, JPG",
      remove: "Remove",
    },
    nonOwnerForm: {
      title: "Owner Representation",
      subtitle: "Please provide the necessary information",
      driver: "Driver",
      driverFirstNamePlaceholder: "First Name",
      driverLastNamePlaceholder: "Last Name",
      owner: "Owner",
      physicalPerson: "Physical Person",
      company: "Company",
      ownerFirstNamePlaceholder: "Owner's First Name",
      ownerLastNamePlaceholder: "Owner's Last Name",
      companyNamePlaceholder: "Company Name",
      procuration: "Power of attorney",
      addDocument: "Add a document",
      fileFormats: "PDF, JPG or PNG",
    },
    verification: {
      title: "Vehicle Verification",
      subtitle: "Enter the registration or chassis number",
      inputLabel: "Registration / Chassis Number",
      inputPlaceholder: "Ex: CE153LC or CH12345678901234567",
      searchButton: "Search",
      invalidFormat: "Invalid format. Please enter a valid registration or chassis number.",
      networkError: "Network error. Please check your connection and try again.",
      ownerInfoIncomplete: "Owner information is incomplete. Please fill it in before continuing.",
      serverResponseInvalid: "Invalid server response. Please try again.",
      serverError: "Server error during verification file creation.",
      serverErrorDossier: "Server error during file creation.",
      resultFound: "Result Found",
      registration: "Registration",
      insured: "Insured",
      expiry: "Expiry",
      infoMatches: "The information matches.",
      infoDoesNotMatch: "The information does not match.",
      continueVerification: "Continue Verification",
      vehicleNotFound: "Vehicle Not Found",
      vehicleNotFoundMessage: "The vehicle was not found in the database.\nIt will be registered as a new registration.",
      input: "Input",
      registeredOwners: "Registered Owners",
      
      // ✅ NEW: Driver found verification translations
      driverFound: "Driver found",
      driverFoundMessage: "You are already registered as a driver for this vehicle.",
      licenseNumber: "License",
    },
    consent: {
      title: "Data Processing Consent",
      subtitle: "In accordance with Law No. 2024/017 of December 23, 2024",
      viewAgreement: "View Full Agreement",
      agreementText: "I consent to the processing of my personal data as part of the vehicle verification procedure.",
      acceptConditions: "I accept the terms and conditions",
      accept: "Accept",
      fullAgreementTitle: "Data Processing Agreement",
      readFullAgreement: "Read full agreement",
    },
    documentUpload: {
      registrationTitle: "Registration Certificate",
      registrationSubtitle: "Add the vehicle registration certificate documents",
      transportTitle: "Public Transport Card",
      transportSubtitle: "Add the public transport card (blue card)",
      taxReceiptTitle: "Tax Payment Receipt",
      taxReceiptSubtitle: "Add the tax payment receipt",
      licenceTitle: "Transport License",
      licenceSubtitle: "Add the ordinary transport license",
      cameraPermissionDenied: "Camera permission denied.",
      noVerificationFound: "No verification file found.",
      addAtLeastOne: "Please add at least one document.",
      uploadError: "Error uploading the registration certificate.",
      transportUploadError: "Error uploading the transport card.",
      taxReceiptUploadError: "Error uploading the tax payment receipt.",
      licenceUploadError: "Error uploading the transport license.",
      takePhoto: "Take a photo",
      photoHint: "Photograph the registration certificate",
      transportPhotoHint: "Photograph the transport card",
      taxReceiptPhotoHint: "Photograph the tax receipt",
      licencePhotoHint: "Photograph the license",
      importFile: "Import a file",
      fileFormats: "PDF, PNG or JPG",
      remove: "Remove",
      next: "Next",
      skip: "Skip (I don't have this document)",
      takePhoto: "Take Photo",
      importFile: "Import File",
      cameraPermissionDenied: "Camera permission denied",
      selectionError: "Error selecting file",
      uploadError: "Error uploading file",
      skip: "Skip",
      importImage: "Import an image"
    },
    completion: {
      doc1: "Public Transport Card (Blue Card)",
      doc2: "Vehicle Registration Certificate",
      doc3: "Tax Payment Receipt",
      doc4: "Ordinary Transport License",
      completedBadge: "✅ Completed",
      submittedTitle: "File submitted for owner, driver and vehicle",
      requestedTitle: "Document submission step completed",
      submittedSubtitle: "Your file has been successfully submitted and is pending verification. You will receive a notification when your QR codes are generated.",
      pendingTitle: "Your QR code is being generated.",
      pendingMessage: "Your QR code is being generated. You will receive a notification when they are ready.",
      requestedSubtitle_part1:"You have completed the required document submission step.",
      checklist: "Checklist",
      documentAdded: "Document added",
      completeMessage: "Your file is now complete.",
      errorMessage: "Error finalizing the file.",
      finish: "Finish",
      startNewForm: "Start a new form",
      finishVerification: "Complete the Identification",
      qrCodeTitle: "Vehicle's QR Code",
      qrCodeDescription: "Your unique QR code for digital verification",
      downloadQRCode: "Download Vehicle QR Code (PDF)",
      downloadQRTitle: "Download Vehicle QR Code",
      downloadQRMessage: "Do you want to download the QR code in PDF format?",
      download: "Download",
      downloadSuccess: "Download successful",
      downloadSuccessMessage: "The QR code has been downloaded successfully",
      downloadError: "Error during download",
      completeError: "Error while completing the verification",
      docsStatusTitle: "Document status",
      driverQrTitle: " Driver's QR Code",
      downloadDriverQr: "Download Driver QR Code",
      yourDriverQrTitle: "Your Driver QR Code",
      infoTitle: "ℹ️ Information",
      driverOnlyQrInfo:"Only your driver QR code was generated. The vehicle QR code can only be generated after providing the procuration.",
      summaryTitle: "Application Summary",
      summaryDescription: "Download a summary document containing all the information you provided, including the status of your documents.",
      downloadSummary: "Download Summary",
      pdfGenerated: "Summary PDF has been generated successfully",
      pdfError: "Error generating the summary PDF",
  docs: {
    carRegistration: "Registration Certificate",
    transportCard: "Transport Card",
    taxReceipt: "Tax Receipt",
    transportLicence: "Transport License",
    procuration: "Power of attorney",

    status: {
      ignored: "Document skipped",
      notProvided: "Not provided",
      notProvidedFemale: "Not provided",
      provided: "Provided",
      filesCount: "{{count}} file(s)",
    },

    blockTitle: "⚠️ Warning",
    back: "Back",
    continue: "Continue",
  },
  vehicleQrBlockedReason:"The vehicle QR code cannot be generated until power of attorney is provided.",

    },
    install: {
      title: "Install application",
      iosInstructions: "On iPhone/iPad: Tap Share → Add to Home Screen.",
      promptMessage: "Install application on your device for faster access and offline support.",
      manualInstructions: "To install: open the browser menu ⋮ and choose Install app.",
      installNow: "Install now",
      later: "Later",
    },
    payment: {
      title: "Communal Stamp",
      subtitle: "Law No. 2017/015 of December 20, 2017, setting the current rate at 500 CFA Francs.",
      amount: "Amount to pay",
      amountHint: "Processing fee",
      selectProvider: "Select your provider",
      mtnHint: "MTN Mobile Money",
      orangeHint: "Orange Money",
      phoneNumber: "Phone Number",
      phoneNumberHint: "Enter your Mobile Money or Orange Money number",
      invalidPhoneNumber: "Invalid number. Format: 6XX XXX XXX or 2XX XXX XXX",
      validPhoneNumber: "Valid number",
      payNow: "Pay {amount} FCFA",
      paymentInitiated: "Payment initiated",
      confirmOnPhone: "A confirmation message has been sent to {provider}. Please enter your PIN on your phone to confirm the payment of {amount} FCFA.",
      waitingConfirmation: "Waiting for confirmation...",
      continueToNext: "Continue to next step",
      verificationId: "Verification ID",
      instructionsTitle: "Important Instructions",
      instructions: "1. Select your provider (MTN or Orange)\n2. Enter your Mobile Money phone number\n3. Click 'Pay now'\n4. Confirm payment on your phone\n5. Wait for automatic confirmation",
      statusSuccess: "✅ Payment confirmed",
      statusPending: "⏳ Waiting for confirmation",
      statusFailed: "❌ Payment failed",
      statusHint: "Checking status...",
      error: "Error",
      enterPhoneNumber: "Please enter your phone number",
      noVerificationId: "No verification ID found",
      initiationFailed: "Payment initiation failed",
      networkError: "Network error. Please try again.",
      waitForConfirmation: "Please wait for payment confirmation",
      successTitle: "Payment Successful",
      successMessage: "Your payment of {amount} FCFA has been successfully confirmed.",
      paymentFailed: "Payment failed or was cancelled. Please try again.",
      alreadyPaidTitle: "Already Paid",
      alreadyPaidMessage: "You have already paid for this verification. Moving to next step...",
      checkingPaymentStatus: "Checking payment status...",
    },
    vehicleNotFoundForm: {
      title: "Vehicle Not Found",
      titleNotMatched: "Register Vehicle",
      subtitle: "Fill in all the details to register the vehicle.",
      back: "Back",
      enteredIdentifierTitle: "Entered identifier",
      ownerSectionTitle: "Owner information",
      vehicleSectionTitle: "Vehicle information",
      ownerTypeLabel: "Owner type",
      ownerTypePhysical: "Individual",
      ownerTypeCompany: "Company",
      ownerNameLabelPhysical: "Owner name",
      ownerNameLabelCompany: "Company name",
      ownerNamePlaceholderPhysical: "E.g. John Doe",
      ownerNamePlaceholderCompany: "E.g. NGONO LTD",
      sexLabel: "Sex",
      male: "Male",
      female: "Female",
      phoneLabel: "Phone",
      phonePlaceholder: "E.g. 6XX XXX XXX",
      emailLabel: "Email",
      emailPlaceholder: "Enter email address",
      brandLabel: "Brand",
      brandOther: "Other",
      specifyBrandLabel: "Specify the brand",
      specifyBrandPlaceholder: "E.g. Mercedes",
      firstRegistrationDateLabel: "First registration date",
      firstRegistrationDatePlaceholder: "DD/MM/YYYY",
      chassisLabel: "Chassis number",
      chassisPlaceholder: "E.g. JT123...",
      seatsLabel: "Number of seats",
      seatsPlaceholder: "E.g. 5",
      insuranceStickerLabel: "Valid insurance sticker?",
      yes: "Yes",
      no: "No",
      submit: "Continue",
      invalidSeats: "Invalid number of seats.",
      invalidFirstRegistrationDate: "Invalid first registration date (DD/MM/YYYY or YYYY-MM-DD).",
      serverError: "Server error.",
      portierNumberLabel: "Door number",
      portierNumberPlaceholder: "E.g. 12345",
    },
    vehicleAdminDocs: {
      iDontHaveTechInspection: "I don’t have the technical inspection document",
      iDontHaveBlueCard: "I don’t have the blue card",
      iDontHaveOwnerLicense: "I don’t have the owner’s category license",
      iDontHaveMunicipalityTax: "I don’t have the municipality tax number",
      iDontHaveDriverCapacity: "I don’t have the driver’s capacity number",
      iDontHaveMacaron: "I don’t have the macaron document",
      iDontHaveSecureBadge: "I don’t have the secure badge",
    },
    errors: {
      invalidName: "Invalid name",
    },
    driverManager: {
      title: "Drivers",
      addDriver: "+ Add Driver",
      loadingDrivers: "Loading drivers...",
      driverTitle: "Driver {{number}}",
      existingInDatabase: " (Existing in Database)",
      modified: " (Modified)",
      cannotRemove: "Cannot Remove",
      cannotRemoveMessage: "This driver already exists in the database and cannot be removed from here.",
      searchPlaceholder: "Search by CIN or License Number",
      driverFound: "Driver Found",
      driverFoundMessage: "Existing driver information has been loaded.",
      driverSyndicate: "Driver's Union",
      driverSyndicatePlaceholder: "Driver's Union",
      alreadySaved: "Already saved",
      alreadySavedMessage: "This driver has already been saved.",
      saved: "Saved",
      alreadySavedInfo: "This driver has been successfully saved. You can continue.",
      noLongerDriver: "No longer a driver of this vehicle",
      sections: {
        basicInfo: "Basic Information",
        licenseInfo: "License Information",
        capacityInfo: "Professional Capacity",
        secureBadge: "Security Badge",
        documents: "Documents"
      },
      fields: {
        fullName: "Full Name *",
        phone: "Phone *",
        email: "Email",
        birthDate: "Birth Date (DD/MM/YYYY)",
        gender: "Gender",
        cinNumber: "CIN Number",
        taxpayerNumber: "Taxpayer Number",
        licenseNumber: "License Number",
        driverSyndicate: "Driver's Union",
        licenseCategory: "License Category",
        selectCategory: "Select a category",
        capacityNumber: "Capacity Number",
        capacityNumberPlaceholder: "Ex: CAP-12345",
        secureBadge: "Security Badge number"
      },
      dontHaveCapacity: "I don't have a capacity document",
      dontHaveSecureBadge: "I don't have a security badge",
      documents: {
        photo: "Driver's Photo",
        license: "Driver's License",
        idCard: "ID Card",
        capacity: "Capacity Document",
        skipped: "Skipped (document not provided)"
      },
      ocumentUploaded: "Document uploaded successfully",
      documentStoredLocally: "Document selected. It will be saved when the driver is saved.",
      saveToUpload: "Save the driver first to upload documents",
      driverAndDocsSaved: "Driver and documents saved successfully",
      allDriversSaved: "All drivers have been saved successfully",
      uploadDocumentsHint: "Upload driver documents",
      driverSaved: "Driver saved successfully",
      
      // Status
      saved: "Saved",
      uploading: "Uploading...",
      uploadError: "Error uploading document",
      genders: {
        male: "Male",
        female: "Female"
      },
      
      // ✅ NEW: Driver form errors
      errors: {
        invalidPhone: "Invalid phone number"
      }
    },
    vehicleNotMatched: {
      title: "Owner Mismatch",
      subtitle: "The registered owner information does not match. Please provide your complete information.",
      enteredIdentifierTitle: "Vehicle Identifier",
      existingOwnerTitle: "Registered Owner",
      newOwnerTitle: "New Owner Information",
      ownerTypeLabel: "Owner Type",
      idCardTitle: "ID Card",
      takePhoto: "Take a Photo",
      uploadFile: "Upload File",
      idCardHelper: "Accepted formats: PDF, JPG, PNG",
      modalMessage: "The registered owner does not match your information. Please provide your ID documents to continue.",
      provideDocuments: "Provide My Documents",
      ownerSyndicateLabel: "Owner's Union",
      ownerSyndicatePlaceholder: "Owner's Union",
    },
    
    // ✅ NEW: Driver Form translations
    driverForm: {
      addYourself: "Add yourself as a driver",
      subtitle: "Please provide your information to be added as a driver for this vehicle"
    },
  }
};

// Create context
const LanguageContext = createContext();

// Language provider component
export function LanguageProvider({ children }) {
  const [currentLang, setCurrentLang] = useState('fr');

  useEffect(() => {
    // Load saved language preference
    const loadLanguage = async () => {
      try {
        const savedLang = await AsyncStorage.getItem('appLanguage');
        if (savedLang && translations[savedLang]) {
          setCurrentLang(savedLang);
        }
      } catch (error) {
        console.error('Error loading language: - index.js:1219', error);
      }
    };
    
    loadLanguage();
  }, []);

  const setLanguage = async (lang) => {
    if (translations[lang]) {
      setCurrentLang(lang);
      try {
        await AsyncStorage.setItem('appLanguage', lang);
      } catch (error) {
        console.error('Error saving language: - index.js:1232', error);
      }
    }
  };

  // const t = (key) => {
  //   const parts = key.split('.');
  //   let value = translations[currentLang];
  //   for (const p of parts) {
  //     value = value?.[p];
  //   }
  //   return value || key;
  // };

  // Update the t function in your LanguageProvider
const t = (key, params = {}) => {
  const parts = key.split('.');
  let value = translations[currentLang];
  
  for (const p of parts) {
    value = value?.[p];
  }
  
  if (typeof value === 'string' && params) {
    // Replace {{param}} with actual values
    return value.replace(/\{\{(\w+)\}\}/g, (match, paramName) => {
      return params[paramName] !== undefined ? params[paramName] : match;
    });
  }
  
  return value || key;
};

  return (
    <LanguageContext.Provider value={{ currentLang, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Custom hook for using translation
export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within LanguageProvider');
  }
  return context;
}
